<!--
  Agenzia — dettaglio: colonna principale (Dati → Tariffe compensi → Driver)
  + riepilogo sticky.

  1) NIENTE CARD "Foglio riepilogo": il foglio per agenzia non ha una
     struttura a schema.

  2) NIENTE pillola "Attiva" in intestazione: l'agenzia non ha un campo di
     stato.

  3) Lo stato del driver usa le etichette del resto del gestionale
     (Attivo / Non attivo) invece di "In servizio / Riposo" del mockup, per
     non introdurre un secondo vocabolario per lo stesso campo.
     ⚠️ `is_alive` vale il CONTRARIO del suo nome: operativo = false.
-->
<template>
  <div
    v-loading="loading"
    class="adt"
  >
    <div class="adt__head">
      <button
        type="button"
        class="adt__back"
        @click="goBack"
      >
        ‹
      </button>
      <div class="adt__htitle">
        <h1>{{ agency.company_name || 'Agenzia' }}</h1>
        <div class="adt__idmono">
          <template v-if="referente">Ref. {{ referente }} · </template>
          {{ drivers.length }} driver
        </div>
      </div>
      <div class="adt__sp" />
      <div class="adt__acts">
        <el-button
          v-if="!editing"
          size="small"
          @click="startEdit"
        >
          ✎ Modifica
        </el-button>
        <template v-else>
          <el-button
            size="small"
            @click="cancelEdit"
          >
            Annulla
          </el-button>
          <el-button
            size="small"
            type="primary"
            :loading="saving"
            @click="save"
          >
            Salva
          </el-button>
        </template>
      </div>
    </div>

    <div class="adt__grid">
      <div class="adt__col">
        <!-- ---------------------------- DATI ---------------------------- -->
        <section class="card">
          <div class="card__hd">
            <h3>Dati agenzia</h3>
          </div>
          <div class="card__bd">
            <div class="datagrid">
              <div
                v-for="f in DATA_FIELDS"
                :key="f.key"
                class="di"
              >
                <div class="di__k">
                  {{ f.label }}
                </div>
                <el-input
                  v-if="editing"
                  v-model="draft[f.key]"
                  size="small"
                />
                <div
                  v-else
                  class="di__v"
                >
                  {{ agency[f.key] || '—' }}
                </div>
              </div>

              <!-- campi deprecati: visibili solo se una riga storica li ha
                   valorizzati, e mai modificabili -->
              <div
                v-for="f in LEGACY_FIELDS"
                v-show="agency[f.key]"
                :key="f.key"
                class="di"
              >
                <div class="di__k">
                  {{ f.label }} <span class="di__dep">non più in uso</span>
                </div>
                <div class="di__v">
                  {{ agency[f.key] }}
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- ---------------------- TARIFFE COMPENSI ---------------------- -->
        <section class="card">
          <div class="card__hd">
            <h3>Tariffe compensi driver</h3>
            <span class="adt__sp" />
            <span class="card__note">alimentano il COD6</span>
          </div>
          <div class="card__bd">
            <div class="tar">
              <div
                v-for="t in RATE_FIELDS"
                :key="t.key"
                class="tarbox"
              >
                <div class="tarbox__k">
                  {{ t.label }}
                </div>
                <el-input
                  v-if="editing"
                  v-model.number="draft[t.key]"
                  size="small"
                  type="number"
                  step="0.01"
                />
                <div
                  v-else
                  class="tarbox__v"
                >
                  {{ euro(agency[t.key]) }}{{ t.unit }}
                </div>
                <div class="tarbox__h">
                  {{ t.hint }}
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- --------------------------- DRIVER --------------------------- -->
        <section class="card">
          <div class="card__hd">
            <h3>Driver dell'agenzia</h3>
            <span class="adt__sp" />
            <span class="card__note">{{ drivers.length }} driver</span>
          </div>
          <div class="card__bd card__bd--flush">
            <div
              v-if="!drivers.length"
              class="adt__empty"
            >
              Nessun driver collegato a questa agenzia.
            </div>
            <el-table
              v-else
              :data="drivers"
              size="small"
              style="width: 100%"
              @row-click="openDriver"
            >
              <el-table-column
                label="Driver"
                min-width="200"
              >
                <template #default="scope">
                  <div class="drv">
                    <span
                      class="ava"
                      :class="{ 'ava--jolly': scope.row.is_jolly }"
                    >{{ ini(scope.row) }}</span>
                    <span>{{ fullName(scope.row) }}</span>
                  </div>
                </template>
              </el-table-column>

              <el-table-column
                label="Zona"
                min-width="200"
              >
                <template #default="scope">
                  <span class="zone">{{ zoneOf(scope.row) }}</span>
                </template>
              </el-table-column>

              <el-table-column
                label="Ruolo"
                width="110"
              >
                <template #default="scope">
                  <span
                    class="role"
                    :class="{ 'role--jolly': scope.row.is_jolly }"
                  >{{ scope.row.is_jolly ? 'jolly' : 'driver' }}</span>
                </template>
              </el-table-column>

              <el-table-column
                label="Stato"
                width="130"
              >
                <template #default="scope">
                  <StatusPill
                    :kind="isOperativo(scope.row) ? 'ok' : 'muted'"
                    :label="isOperativo(scope.row) ? 'Attivo' : 'Non attivo'"
                  />
                </template>
              </el-table-column>
            </el-table>
          </div>
        </section>
      </div>

      <!-- ============================== ASIDE ============================ -->
      <div class="adt__aside">
        <section class="card">
          <div class="card__hd">
            <h3>Riepilogo</h3>
          </div>
          <div class="card__bd">
            <div class="kv">
              <div class="kv__r">
                <span class="kv__k">Driver</span>
                <span class="kv__v">
                  {{ drivers.length }}
                  <template v-if="drivers.length">
                    ({{ operativiCount }} operativi)
                  </template>
                </span>
              </div>
              <div class="kv__r">
                <span class="kv__k">Tariffa oraria</span>
                <span class="kv__v">{{ euro(agency.hourly_rate) }}/h</span>
              </div>
              <div class="kv__r">
                <span class="kv__k">Rimborso km</span>
                <span class="kv__v">{{ euro(agency.km_refund_rate) }}/km</span>
              </div>
              <div class="kv__r">
                <span class="kv__k">Compenso notturno</span>
                <span class="kv__v">{{ euro(agency.night_compensation_rate) }}</span>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ElMessage } from 'element-plus';
import agencyAPI from '../../services/agencyAPI.js';
import StatusPill from '../../components/ui/StatusPill.vue';

const DATA_FIELDS = [
  { key: 'company_name', label: 'Ragione sociale' },
  { key: 'name', label: 'Nome referente' },
  { key: 'surname', label: 'Cognome referente' },
  { key: 'mail', label: 'Email' },
  { key: 'telephone_number', label: 'Telefono' },
];

// `document` e `priority` sono marcati DEPRECATO sull'entity ("rimosso dai
// form", resi nullable apposta). Non si offrono in modifica: si mostrano solo
// se una riga storica li ha valorizzati, per non far sparire il dato.
const LEGACY_FIELDS = [
  { key: 'document', label: 'Documento / P.IVA' },
  { key: 'priority', label: 'Priorità' },
];

const RATE_FIELDS = [
  {
    key: 'hourly_rate', label: 'Tariffa oraria', unit: '/h',
    hint: 'compenso base = ore × tariffa',
  },
  {
    key: 'km_refund_rate', label: 'Rimborso km jolly', unit: '/km',
    hint: 'solo per il jolly',
  },
  {
    key: 'night_compensation_rate', label: 'Compenso notturno', unit: '',
    hint: 'a servizio notturno',
  },
];

export default {
  name: 'DetailAgencyPage',
  components: { StatusPill },

  setup() {
    const route = useRoute();
    const router = useRouter();

    const agency = ref({});
    const drivers = ref([]);
    const loading = ref(false);
    const editing = ref(false);
    const saving = ref(false);
    const draft = reactive({});

    const referente = computed(() =>
      [agency.value.name, agency.value.surname].filter(Boolean).join(' ')
    );

    const operativiCount = computed(
      () => drivers.value.filter((d) => agencyAPI.isDriverOperativo(d)).length
    );

    const load = async () => {
      loading.value = true;
      const id = route.params.id;
      try {
        const res = await agencyAPI.getAgencyDetail(id);
        agency.value = res.data ?? {};
      } catch (e) {
        console.error('Caricamento agenzia fallito:', e);
        ElMessage.error('Impossibile caricare l\'agenzia');
      } finally {
        loading.value = false;
      }

      // I driver non arrivano con l'agenzia: `drivers` non è fra i join
      // consentiti su /agency, quindi si chiedono a /driver.
      try {
        drivers.value = await agencyAPI.getDriversOfAgency(id);
      } catch (e) {
        console.warn('Elenco driver non disponibile:', e?.message);
        drivers.value = [];
      }

      resetDraft();
    };

    const resetDraft = () => {
      const next = {};
      for (const f of DATA_FIELDS) next[f.key] = agency.value[f.key];
      for (const t of RATE_FIELDS) next[t.key] = agency.value[t.key];
      Object.assign(draft, next);
    };

    const startEdit = () => { resetDraft(); editing.value = true; };
    const cancelEdit = () => { resetDraft(); editing.value = false; };

    const save = async () => {
      saving.value = true;
      try {
        await agencyAPI.updateAgency({ id: route.params.id, ...draft });
        ElMessage.success('Agenzia aggiornata');
        editing.value = false;
        await load();
      } catch (e) {
        console.error('Salvataggio agenzia fallito:', e);
        ElMessage.error(
          e?.response?.status === 403
            ? 'Non hai i permessi per modificare questa agenzia'
            : 'Salvataggio non riuscito'
        );
      } finally {
        saving.value = false;
      }
    };

    const fullName = (d) => [d?.name, d?.surname].filter(Boolean).join(' ') || '—';

    const ini = (d) =>
      `${(d?.name || '')[0] || ''}${(d?.surname || '')[0] || ''}`.toUpperCase() || '?';

    // Zone strutturate. Se un driver non è ancora migrato si mostrano le
    // vecchie aree, così si vede a colpo d'occhio chi manca.
    const zoneOf = (d) => {
      if (d.provinces?.length) {
        const regions = [...new Set(d.provinces.map((p) => p.region?.name).filter(Boolean))];
        const sigle = d.provinces.map((p) => p.sigla || p.name).filter(Boolean);
        return [regions.join(', '), sigle.join(', ')].filter(Boolean).join(' · ');
      }
      if (d.areas?.length) return `${d.areas.map((a) => a.area).join(', ')} · da migrare`;
      return '—';
    };

    const euro = (n) =>
      n == null || n === '' ? '—' : `€ ${Number(n).toFixed(2).replace('.', ',')}`;

    const goBack = () => router.push('/agency');
    const openDriver = (d) => { if (d?.id) router.push(`/driver/detail/${d.id}`); };

    onMounted(load);

    return {
      agency, drivers, loading, editing, saving, draft,
      referente, operativiCount, DATA_FIELDS, RATE_FIELDS, LEGACY_FIELDS,
      startEdit, cancelEdit, save, fullName, ini, zoneOf, euro,
      goBack, openDriver,
      isOperativo: agencyAPI.isDriverOperativo,
    };
  },
};
</script>

<style scoped>
.adt { padding: 4px 0 32px; }
.adt__sp { flex: 1; }

.adt__head {
  display: flex; align-items: center; gap: 14px; flex-wrap: wrap; margin: 0 2px 16px;
}
.adt__back {
  width: 36px; height: 36px; flex: none; border-radius: 9px;
  border: 1px solid var(--border-color); background: var(--bg-primary);
  display: grid; place-items: center; cursor: pointer;
  font-size: 16px; color: var(--text-secondary); font-family: inherit;
}
.adt__back:hover { border-color: var(--brand-violet); }
.adt__htitle h1 { margin: 0; font-size: 21px; color: var(--text-primary); }
.adt__idmono { margin-top: 3px; font-size: 12.5px; color: var(--text-tertiary); }
.adt__acts { display: flex; gap: 8px; flex-wrap: wrap; }

.adt__grid {
  display: grid; grid-template-columns: 1fr 330px; gap: 16px; align-items: start;
}
@media (max-width: 960px) { .adt__grid { grid-template-columns: 1fr; } }
.adt__col { display: flex; flex-direction: column; gap: 16px; }
.adt__aside {
  position: sticky; top: 16px; display: flex; flex-direction: column; gap: 16px;
}
@media (max-width: 960px) { .adt__aside { position: static; } }

.card {
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
}
.card__hd {
  display: flex; align-items: center; gap: 10px;
  padding: 13px 16px; border-bottom: 1px solid var(--border-color);
}
.card__hd h3 { margin: 0; font-size: 14px; color: var(--text-primary); }
.card__note { font-size: 12px; color: var(--text-tertiary); }
.card__bd { padding: 16px; }
.card__bd--flush { padding: 0; }

.adt__empty { font-size: 13px; color: var(--text-tertiary); padding: 16px; }

.datagrid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px 22px; }
@media (max-width: 560px) { .datagrid { grid-template-columns: 1fr; } }
.di__k {
  font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.05em;
  color: var(--text-tertiary); font-weight: 700; margin-bottom: 3px;
}
.di__v { font-weight: 600; font-size: 14px; color: var(--text-primary); }
.di__dep {
  font-weight: 600; text-transform: none; letter-spacing: 0;
  color: var(--state-attn);
}

.tar { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
@media (max-width: 560px) { .tar { grid-template-columns: 1fr; } }
.tarbox {
  border: 1px solid var(--border-color); border-radius: 10px;
  padding: 12px 14px; background: var(--bg-secondary);
}
.tarbox__k {
  font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em;
  color: var(--text-tertiary); font-weight: 700;
}
.tarbox__v {
  font-size: 19px; font-weight: 750; margin-top: 4px;
  font-variant-numeric: tabular-nums; color: var(--text-primary);
}
.tarbox__h { font-size: 11px; color: var(--text-tertiary); margin-top: 2px; }

.drv { display: flex; align-items: center; gap: 9px; }
.ava {
  width: 28px; height: 28px; flex: none; border-radius: 50%;
  display: grid; place-items: center; font-size: 10.5px; font-weight: 700;
  color: #fff; background: var(--avatar-bg);
}
.ava--jolly { background: var(--svc-fleet); }
.zone { color: var(--text-secondary); font-size: 12px; }
.role {
  font-size: 10px; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.03em; color: var(--text-tertiary);
  background: var(--bg-tertiary); border: 1px solid var(--border-color);
  border-radius: 5px; padding: 1px 6px;
}
.role--jolly { color: var(--svc-fleet); }

.kv { display: flex; flex-direction: column; }
.kv__r {
  display: flex; justify-content: space-between; align-items: center;
  gap: 12px; padding: 9px 0; border-bottom: 1px solid var(--border-color);
}
.kv__r:last-child { border-bottom: 0; }
.kv__k { color: var(--text-tertiary); font-size: 12.5px; flex: none; }
.kv__v {
  font-weight: 600; text-align: right; color: var(--text-primary);
  font-variant-numeric: tabular-nums;
}

.adt :deep(.el-table__row) { cursor: pointer; }
.adt :deep(.el-table th.el-table__cell) {
  background: var(--bg-tertiary) !important;
  color: var(--text-tertiary) !important;
  font-size: 11px; font-weight: 700; letter-spacing: 0.05em;
  padding: 10px 12px; border-bottom: 1px solid var(--border-color);
}
</style>
